// Course detail modal showing aggregate stats (admin view)
import { X, Award, Users } from 'lucide-react'

import { Button } from '@/ui/button'
import { Progress } from '@/ui/progress'
import type { Course } from '@/features/classes/stores'

interface CourseStats {
  courseId: string
  enrolled: number
  completed: number
  inProgress: number
  certificatesIssued: number
  passRate: number | null
}

interface CourseDetailProps {
  course: Course
  stats: CourseStats
  onClose: () => void
}

export function CourseDetail({ course, stats, onClose }: CourseDetailProps) {
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-navy rounded-t-2xl p-6 text-white text-center relative">
          <button onClick={onClose} className="absolute top-4 right-4 p-1.5 rounded-lg hover:bg-white/10 text-white/70">
            <X className="w-4 h-4" />
          </button>
          <h2 className="text-xl font-bold">{course.name}</h2>
          <div className="flex items-center justify-center gap-4 mt-3 text-sm text-slate-400">
            <span className="flex items-center gap-1"><Award className="w-4 h-4" />Válido {course.validYears} año{course.validYears > 1 ? 's' : ''}</span>
          </div>
        </div>

        <div className="p-6 space-y-5">
          {/* Stats grid */}
          <div className="grid grid-cols-3 gap-3">
            {[
              { label: 'Inscritos',    value: stats.enrolled,          color: 'text-navy',  bg: 'bg-gray-50' },
              { label: 'Completados', value: stats.completed,          color: 'text-green-600',   bg: 'bg-green-50' },
              { label: 'Certificados', value: stats.certificatesIssued, color: 'text-primary',  bg: 'bg-orange-50' },
            ].map((s) => (
              <div key={s.label} className={`${s.bg} rounded-xl p-3 text-center`}>
                <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
                <p className="text-xs text-slate-500 mt-0.5">{s.label}</p>
              </div>
            ))}
          </div>

          {/* Pass rate */}
          <div>
            <div className="flex justify-between mb-1.5 text-sm">
              <span className="font-medium text-navy">Tasa de aprobación</span>
              <span className="text-primary font-semibold">
                {stats.passRate !== null ? `${stats.passRate}%` : '—'}
              </span>
            </div>
            <Progress value={stats.passRate ?? 0} />
          </div>

          <div className="flex gap-2 pt-1">
            <Button variant="outline" className="flex-1" onClick={onClose}>Cerrar</Button>
            <Button className="flex-1">
              <Users className="w-4 h-4" /> Ver Todos los Estudiantes
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

export type { CourseStats }
